// src/components/layout/MyFooter.jsx
import {
  Box,
  Container,
  IconButton,
  Stack,
  Link,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import LocalDiningRoundedIcon from "@mui/icons-material/LocalDiningRounded";
import Logo from "../../assets/logo.png";

const exploreLinks = [
  { label: "Hem", to: "/" },
  { label: "Recept", to: "/recipes" },
  { label: "Näringsämnen", to: "/nutritions" },
  { label: "Artiklar", to: "/articles" },
];

const aboutLinks = [
  { label: "Om oss", to: "/about" },
  { label: "FAQs", to: "/faqs" },
];

export default function MyFooter() {
  return (
    <Box
      component="footer"
      sx={{
        position: "relative",
        bgcolor: "surfaces.footer",
        color: "surfaces.onDark",
        mt: 0,
        pt: { xs: 6, md: 8 },
        pb: 3,
        overflow: "hidden",
      }}
    >
      {/* Decorative wave divider at the top */}
      <Box
        component="svg"
        viewBox="0 0 1440 80"
        preserveAspectRatio="none"
        aria-hidden="true"
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: { xs: 32, md: 56 },
          display: "block",
          color: "background.default",
        }}
      >
        <path
          d="M0,40 C240,80 480,0 720,32 C960,64 1200,16 1440,48 L1440,0 L0,0 Z"
          fill="currentColor"
        />
      </Box>

      {/* Floating accent leaf */}
      <Box
        sx={{
          position: "absolute",
          top: { xs: 18, md: 32 },
          right: { xs: -28, md: -40 },
          opacity: 0.12,
          color: "accent.main",
          fontSize: { xs: 180, md: 260 },
          pointerEvents: "none",
          transform: "rotate(-18deg)",
        }}
        aria-hidden="true"
      >
        <LocalDiningRoundedIcon fontSize="inherit" />
      </Box>

      <Container maxWidth="lg" sx={{ position: "relative" }}>
        <Box
          sx={{
            display: "grid",
            gap: { xs: 4, md: 6 },
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1.4fr 1fr 1fr",
              md: "1.6fr 1fr 1fr 1.2fr",
            },
            alignItems: "start",
            mb: 4,
          }}
        >
          {/* Brand block */}
          <Stack spacing={1.5} sx={{ maxWidth: 360 }}>
            <Box
              component={RouterLink}
              to="/"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                textDecoration: "none",
                color: "inherit",
              }}
            >
              <Box
                component="img"
                src={Logo}
                alt="CreativeInsights"
                sx={{
                  height: 40,
                  width: "auto",
                  filter: "brightness(0) invert(1)",
                  opacity: 0.95,
                }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{ color: "surfaces.onDark", opacity: 0.85, lineHeight: 1.6 }}
            >
              Visualisera näringen i din mat och upptäck nya sätt att äta
              smartare — ett recept i taget.
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "surfaces.onDark",
                opacity: 0.65,
                fontSize: 11,
                letterSpacing: 0.3,
              }}
            >
              Näringsdata från{" "}
              <Box
                component="a"
                href="https://www.livsmedelsverket.se/livsmedel-och-innehall/naringsamne/livsmedelsdatabasen"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "accent.main",
                  fontWeight: 700,
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Livsmedelsverket
              </Box>
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
              {[
                {
                  label: "Instagram",
                  href: "https://instagram.com",
                  icon: <InstagramIcon fontSize="small" />,
                },
                {
                  label: "Facebook",
                  href: "https://facebook.com",
                  icon: <FacebookIcon fontSize="small" />,
                },
                {
                  label: "E-post",
                  href: "mailto:creativeinsights.recipe@gmail.com",
                  icon: <MailOutlineIcon fontSize="small" />,
                },
              ].map((s) => (
                <IconButton
                  key={s.label}
                  component="a"
                  href={s.href}
                  target={s.href.startsWith("http") ? "_blank" : undefined}
                  rel={
                    s.href.startsWith("http") ? "noopener noreferrer" : undefined
                  }
                  aria-label={s.label}
                  size="small"
                  sx={{
                    color: "surfaces.onDark",
                    bgcolor: "rgba(255,255,255,0.08)",
                    transition: "all .2s ease",
                    "&:hover": {
                      bgcolor: "accent.main",
                      color: "primary.main",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  {s.icon}
                </IconButton>
              ))}
            </Stack>
          </Stack>

          {/* Explore column */}
          <FooterColumn title="Utforska" links={exploreLinks} />

          {/* About column */}
          <FooterColumn title="Om" links={aboutLinks} />

          {/* Contact / CTA column */}
          <Stack spacing={1.25}>
            <ColumnTitle>Hör av dig</ColumnTitle>
            <Link
              component="a"
              href="mailto:creativeinsights.recipe@gmail.com"
              underline="none"
              sx={{
                color: "surfaces.onDark",
                opacity: 0.85,
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                fontSize: 14,
                "&:hover": { color: "accent.main", opacity: 1 },
              }}
            >
              <MailOutlineIcon fontSize="small" />
              creativeinsights.recipe@gmail.com
            </Link>
          </Stack>
        </Box>

        {/* Bottom bar */}
        <Box
          sx={{
            pt: 2.5,
            borderTop: (t) =>
              `1px solid ${t.palette.surfaces.divider ?? "rgba(255,255,255,0.15)"}`,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 1.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: "surfaces.onDark", opacity: 0.8, letterSpacing: 0.3 }}
          >
            © {new Date().getFullYear()} CreativeInsights · Alla rättigheter
            förbehållna
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "surfaces.onDark",
              opacity: 0.8,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            Gjort med
            <Box
              component={FavoriteRoundedIcon}
              sx={{ fontSize: 14, color: "accent.main" }}
            />
            i Sverige
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

function ColumnTitle({ children }) {
  return (
    <Typography
      variant="overline"
      sx={{
        color: "accent.main",
        fontWeight: 700,
        letterSpacing: 1.2,
        fontSize: 12,
      }}
    >
      {children}
    </Typography>
  );
}

function FooterColumn({ title, links }) {
  return (
    <Stack spacing={1.25}>
      <ColumnTitle>{title}</ColumnTitle>
      {links.map((l) => (
        <Link
          key={l.label}
          component={RouterLink}
          to={l.to}
          underline="none"
          sx={{
            color: "surfaces.onDark",
            opacity: 0.85,
            fontSize: 14,
            fontWeight: 500,
            width: "fit-content",
            position: "relative",
            transition: "all .2s ease",
            "&::after": {
              content: '""',
              position: "absolute",
              left: 0,
              bottom: -2,
              width: 0,
              height: "2px",
              bgcolor: "accent.main",
              transition: "width .25s ease",
            },
            "&:hover": {
              color: "accent.main",
              opacity: 1,
            },
            "&:hover::after": { width: "100%" },
          }}
        >
          {l.label}
        </Link>
      ))}
    </Stack>
  );
}

